<template>
	<div v-if="scoringEnabled" class="points-statement-zone">
		<div class="points-statement-zone__row">
			<v-icon class="points-statement-zone__icon">trophy</v-icon>
			<span class="points-statement-zone__label">{{ t('PointsOnThisOffer') }}</span>

			<div v-if="historyLoaded" class="points-statement-zone__history">
				<template v-if="hasHistory">
					<span v-if="pointsSums.validated > 0" class="points-statement-zone__stat points-statement-zone__stat--validated">
						<v-icon>check_circle</v-icon>
						<strong>{{ formatPoints(pointsSums.validated) }} {{ t('pts') }}</strong>
						{{ t('Validated') }}
					</span>
					<span v-if="pointsSums.pending > 0" class="points-statement-zone__stat points-statement-zone__stat--pending">
						<v-icon>hourglass_empty</v-icon>
						<strong>{{ formatPoints(pointsSums.pending) }} {{ t('pts') }}</strong>
						{{ t('Pending') }}
					</span>
					<span v-if="pointsSums.denied > 0" class="points-statement-zone__stat points-statement-zone__stat--denied">
						<v-icon>cancel</v-icon>
						<strong>{{ formatPoints(pointsSums.denied) }} {{ t('pts') }}</strong>
						{{ t('Denied') }}
					</span>
				</template>
				<span v-else class="points-statement-zone__empty">
					<v-icon>info</v-icon>
					{{ t('NoPointsRequestYet') }}
				</span>
			</div>

			<v-spacer />

			<a v-if="historyLoaded && hasHistory" class="points-statement-zone__detail-link" @click.prevent="openHistory">{{ t('SeeDetail') }} ›</a>
			<PointsClaimButton v-if="points > 0 && !hideClaim" :campaign-id="context.campaignId" :offer-id="context.offerId" :appointment-uid="context.appointmentUid" :delivery-location-id="context.deliveryLocationId" :promo-name="context.promoData?.promoName || ''" :points="points" :overlay-target="overlayTarget" :campaign-offer-start-date="context.campaignOfferStartDate" :campaign-offer-end-date="context.campaignOfferEndDate" :readonly="!!context.readonly" />
		</div>
	</div>
</template>

<script setup>
import { ref, computed, inject, watch, onMounted, onBeforeUnmount } from 'vue';
import PointsClaimButton from './PointsClaimButton.vue';
import { calculatePoints, isScoringMatrixAvailable } from '@/services/pointsMatrixService';
import { getPointsHistory, matchesOfferContext, roundPoints, formatPoints, isScoringPeriodClosed } from '@/api/sales/points';
import { useTranslator } from '@/composables/useTranslator';

const props = defineProps({
	context: { type: Object, default: null },
	hideClaim: { type: Boolean, default: false },
});
const emit = defineEmits(['history-change']);

const t = useTranslator();
const overlayTarget = inject('overlayTarget', null);

const periodClosed = ref(false);
const historyLoaded = ref(false);
const offerHistory = ref([]);

const scoringEnabled = computed(() => !!props.context?.scoringEnabled && isScoringMatrixAvailable() && !periodClosed.value);
const points = computed(() => {
	if (!scoringEnabled.value) return 0;
	const data = props.context?.promoData || {};
	return calculatePoints({ volumeUC: data.volumeUC, meaCount: data.meaCount, presentCount: data.presentCount });
});

const pointsSums = computed(() => ({
	validated: roundPoints(offerHistory.value.filter((e) => e.status === 'VALIDATED' || e.status === 'AUTO').reduce((sum, e) => sum + roundPoints(e.pointsValidated ?? e.pointsClaimed ?? 0), 0)),
	pending: roundPoints(offerHistory.value.filter((e) => e.status === 'PENDING').reduce((sum, e) => sum + roundPoints(e.pointsClaimed ?? 0), 0)),
	denied: roundPoints(offerHistory.value.filter((e) => e.status === 'DENIED').reduce((sum, e) => sum + roundPoints(e.pointsClaimed ?? 0), 0)),
}));

const hasHistory = computed(() => offerHistory.value.length > 0);

const offerContext = () => ({
	campaignId: props.context?.campaignId ?? null,
	offerId: props.context?.offerId ?? null,
	campaignOfferStartDate: props.context?.campaignOfferStartDate ?? null,
	campaignOfferEndDate: props.context?.campaignOfferEndDate ?? null,
	deliveryLocationId: props.context?.deliveryLocationId ?? null,
});

async function loadOfferHistory() {
	if (!props.context?.scoringEnabled || !isScoringMatrixAvailable()) return;
	try {
		periodClosed.value = await isScoringPeriodClosed();
		if (periodClosed.value) {
			offerHistory.value = [];
			return;
		}
		const history = await getPointsHistory();
		const context = offerContext();
		offerHistory.value = (history ?? []).filter((e) => matchesOfferContext(e, context));
	} catch {
		offerHistory.value = [];
	} finally {
		historyLoaded.value = true;
		emit('history-change', hasHistory.value);
	}
}

function openHistory() {
	window.dispatchEvent(new CustomEvent('open-vue-points-dialog', { detail: offerContext() }));
}

const contextKey = computed(() => JSON.stringify([offerContext(), !!props.context?.scoringEnabled]));
watch(contextKey, loadOfferHistory);

onMounted(() => {
	window.addEventListener('points-updated', loadOfferHistory);
	loadOfferHistory();
});

onBeforeUnmount(() => {
	window.removeEventListener('points-updated', loadOfferHistory);
});
</script>
